/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import { useState, useMemo, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Alert from 'common/components/Alert';
import MotiliTextButton, {
    MotiliTextButtonGroup,
} from 'common/components/buttons/MotiliTextButton';
import ChecklistSuccessfullyGeneratedPdfModal from 'common/components/work-order/ChecklistSuccessfullyGeneratedPdfModal';
import { WORK_ORDER_STATUS } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    isContractorAccount,
    isMotiliAdmin,
} from 'common/services/AuthService';
import {
    calculateProgress,
    downloadChecklist,
    downloadChecklists,
} from 'admin/services/AdminChecklistService';
import { Flex, LoadingSpinner } from '@motili/ui';
import { Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { SimpleDataGrid } from 'common/components/data-grid/SimpleDataGrid';
import styled, { css } from 'styled-components';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import { StyledDownloadIconButton } from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import Config from 'Config';
import { DownloadIconButton } from 'common/components/buttons/ActionIconButtons';
import AdminWorkOrderRequirementsChecklistViewModal from '../AdminWorkOrderRequirementsChecklistViewModal';
import AdminWorkOrderRequirementsChecklistCompleteModal from '../AdminWorkOrderRequirementsChecklistCompleteModal';
import AdminWorkOrderRequirementsChecklistRemoveModal from '../AdminWorkOrderRequirementsChecklistRemoveModal';
import AdminWorkOrderRequirementsChecklistAddModal from '../AdminWorkOrderRequirementsChecklistAddModal';
import AdminWorkOrderRequirementsChecklistRevertModal from '../AdminWorkOrderRequirementsChecklistRevertModal';

const defaultSx = {
    fontFamily: 'Nunito Sans, sans-serif',
};

const modalStateName = {
    addChecklist: 'addChecklist',
    viewChecklist: 'viewChecklist',
    complete: 'complete',
    remove: 'remove',
    revert: 'revert',
    download: 'download',
};

const AdminWorkOrderRequirementsChecklists = props => {
    const { checklists, checklistOptions, canAddChecklists, workOrder } = props;
    const [busy, setBusy] = useState(false);
    const [openActionMenuId, setOpenActionMenuId] = useState(null);
    const [displayModalState, setDisplayModalState] = useState({
        [modalStateName.addChecklist]: {
            show: false,
        },
        [modalStateName.viewChecklist]: {
            show: false,
            checklist: {},
        },
        [modalStateName.complete]: {
            show: false,
            checklist: {},
        },
        [modalStateName.remove]: {
            show: false,
            checklist: {},
        },
        [modalStateName.revert]: {
            show: false,
            checklist: {},
        },
        [modalStateName.download]: {
            show: false,
            checklist: {},
        },
    });

    const closeModal = () => {
        let closeModal = {};
        _.chain(modalStateName)
            .filter(modalName => displayModalState[modalName].show)
            .forEach(modalName => {
                closeModal = {
                    ...closeModal,
                    [modalName]: {
                        ...displayModalState[modalName],
                        show: false,
                    },
                };
            })
            .value();

        setDisplayModalState(prevState => ({
            ...prevState,
            ...closeModal,
        }));
    };

    const displayModal = useCallback((modal, checklist) => {
        setDisplayModalState(prevState => ({
            ...prevState,
            [modal]: {
                show: true,
                checklist,
            },
        }));
    }, []);

    const handleDownloadAll = async checklistsToDownload => {
        setBusy(true);
        const start = new Date();
        const res = await downloadChecklists(checklistsToDownload);
        const end = new Date();

        setBusy(false);
        setDisplayModalState(prevState => ({
            ...prevState,
            download: {
                show: true,
                checklist: {
                    checklists: res,
                    timeElapsed: (end - start) / 1000,
                },
            },
        }));
    };

    const handleSaveChecklistOnClick = async (checklist, sections) => {
        await props.handleSaveChecklistOnClick(checklist, sections);
        closeModal();
    };

    const handleCompleteChecklistOnClick = async (requirementId, reason) => {
        await props.handleCompleteChecklistOnClick(requirementId, reason);
        closeModal();
    };

    const handleAddChecklistOnClick = async data => {
        await props.handleAddChecklistOnClick(data);
        closeModal();
    };

    const handleRemoveChecklistOnClick = async (requirementId, reason) => {
        await props.handleRemoveChecklistOnClick(requirementId, reason);
        closeModal();
    };

    const handleRevertChecklistOnClick = async (checklist, reason) => {
        await props.handleRevertChecklistOnClick(checklist, reason);
        closeModal();
    };

    const dataGridColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                headerName: (
                    <StyledChecklistHeader>
                        Checklist name
                    </StyledChecklistHeader>
                ),
                field: 'name',
                renderCell: ({ row }) => (
                    <a
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                            displayModal(
                                modalStateName.viewChecklist,
                                row.checklist
                            )
                        }
                    >
                        <StyledChecklistLink>{row.name}</StyledChecklistLink>
                    </a>
                ),
                flex: 1,
            },
            {
                headerName: (
                    <StyledChecklistHeader>Description</StyledChecklistHeader>
                ),
                field: 'description',
                renderCell: ({ row }) => (
                    <StyledChecklistValue>
                        {row.description}
                    </StyledChecklistValue>
                ),
                flex: 1,
            },
            {
                headerName: (
                    <StyledChecklistHeader>Progress</StyledChecklistHeader>
                ),
                field: 'progress',
                width: 100,
                renderCell: ({ row }) =>
                    row.progress === 100 ? (
                        <StyledChecklistValue
                            style={{ color: Config.color.green }}
                        >
                            100%
                        </StyledChecklistValue>
                    ) : (
                        <StyledChecklistValue>{`${row.progress}%`}</StyledChecklistValue>
                    ),
            },
            {
                headerName: '',
                field: 'action',
                renderCell: ({ row }) => (
                    <div onClick={e => e.stopPropagation()}>
                        <ActionMenu
                            checklist={row.checklist}
                            workOrder={row.workOrder}
                            displayModal={displayModal}
                            openActionMenuId={openActionMenuId}
                            setOpenActionMenuId={setOpenActionMenuId}
                        />
                    </div>
                ),
                width: 90,
            },
        ];
        return columns;
    }, [
        displayModal,
        workOrder,
        checklists,
        openActionMenuId,
        setOpenActionMenuId,
    ]);

    const dataGridRow = useMemo(() => {
        const rows = _.map(checklists, checklist => {
            const id = checklist.id;
            const name = checklist.requirement.name;
            const description = checklist.requirement.description;
            const progress = calculateProgress(checklist.requirement.sections);
            const action = {};
            return {
                id,
                name,
                description,
                progress,
                action,
                checklist,
                workOrder,
            };
        });
        return rows;
    }, [checklists, workOrder]);

    const noChecklistOverlay = useCallback(() => {
        return (
            <NoChecklists
                displayModalFunc={displayModal}
                canAddChecklists={canAddChecklists}
            />
        );
    }, [canAddChecklists, displayModal]);

    return (
        <>
            <AdminWorkOrderRequirementsChecklistViewModal
                handleCloseOnClick={closeModal}
                checklist={displayModalState.viewChecklist.checklist}
                handleSaveChecklistOnClick={handleSaveChecklistOnClick}
                workOrderStatus={workOrder.statusId}
                workOrder={workOrder}
                accountId={props.accountId}
                isOpen={displayModalState.viewChecklist.show}
            />
            <AdminWorkOrderRequirementsChecklistCompleteModal
                handleCloseOnClick={closeModal}
                checklist={displayModalState.complete.checklist}
                handleCompleteChecklistOnClick={handleCompleteChecklistOnClick}
                isOpen={displayModalState.complete.show}
            />
            <AdminWorkOrderRequirementsChecklistRemoveModal
                handleCloseOnClick={closeModal}
                checklist={displayModalState.remove.checklist}
                handleRemoveChecklistOnClick={handleRemoveChecklistOnClick}
                isOpen={displayModalState.remove.show}
            />
            <AdminWorkOrderRequirementsChecklistAddModal
                checklistOptions={checklistOptions}
                checklists={checklists}
                handleCloseOnClick={closeModal}
                handleAddChecklistOnClick={handleAddChecklistOnClick}
                isOpen={displayModalState.addChecklist.show}
            />
            <AdminWorkOrderRequirementsChecklistRevertModal
                handleCloseOnClick={closeModal}
                handleRevertOnClick={handleRevertChecklistOnClick}
                checklist={displayModalState.revert.checklist}
                isOpen={displayModalState.revert.show}
            />
            {displayModalState.download.show && (
                <ChecklistSuccessfullyGeneratedPdfModal
                    handleCloseOnClick={closeModal}
                    checklists={displayModalState.download.checklist.checklists}
                    timeElapsed={
                        displayModalState.download.checklist.timeElapsed
                    }
                />
            )}
            <Flex direction='column' fullWidth style={{ marginBottom: '16px' }}>
                <Flex justify='space-between' fullWidth>
                    <Typography
                        sx={[
                            defaultSx,
                            {
                                fontSize: '24px',
                                fontWeight: 600,
                            },
                        ]}
                    >
                        Checklists
                    </Typography>
                    {!_.isEmpty(checklists) &&
                        (isMotiliAdmin() || isContractorAccount()) && (
                            <DownloadAllPDFButton
                                onClick={() => handleDownloadAll(checklists)}
                                busy={busy}
                            />
                        )}
                </Flex>
                <Flex fullWidth style={{ zIndex: '1', position: 'relative' }}>
                    <SimpleDataGrid
                        columns={dataGridColumns}
                        rows={dataGridRow}
                        getRowId={row => row.id}
                        getRowHeight={() => 'auto'}
                        disableColumnResize
                        disableColumnSorting
                        disableColumnSelector
                        disableColumnMenu
                        disableColumnFilter
                        disableColumnReorder
                        getRowClassName={params =>
                            params.id === openActionMenuId
                                ? 'row--action-menu-open'
                                : ''
                        }
                        customSx={{
                            '& .MuiDataGrid-columnSeparator': {
                                display: 'flex',
                            },
                            '&.MuiDataGrid-root': {
                                overflow: 'visible',
                            },
                            '& .MuiDataGrid-main': {
                                overflow: 'visible',
                            },
                            '& .MuiDataGrid-cell': {
                                padding: '8px 10px',
                                overflow: 'visible !important',
                                whiteSpace: 'normal !important',
                                wordBreak: 'break-word',
                                zIndex: 1,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                overflow: 'visible !important',
                            },
                            '& .MuiDataGrid-virtualScrollerContent': {
                                width: '100% !important',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                cursor: 'default !important',
                            },
                            '& .MuiDataGrid-topContainer': {
                                position: 'relative',
                            },
                            '& .MuiDataGrid-cellEmpty': {
                                display: 'none',
                            },
                            '--DataGrid-overlayHeight': '96px',
                            '& .MuiDataGrid-cell[data-field="action"]': {
                                overflow: 'visible !important',
                                position: 'relative',
                                zIndex: 10,
                                pointerEvents: 'auto !important',
                            },
                            '& .MuiDataGrid-row': {
                                position: 'relative',
                                zIndex: 1,
                            },
                            '& .MuiDataGrid-row:hover': {
                                zIndex: 1,
                            },
                            '& .MuiDataGrid-row.row--action-menu-open': {
                                zIndex: 100,
                            },
                            '& .MuiDataGrid-overlayWrapper': {
                                position: 'static',
                            },
                        }}
                        slots={{
                            noRowsOverlay: noChecklistOverlay,
                        }}
                    />
                </Flex>
                <Flex justify='flex-end' style={{ zIndex: '0' }}>
                    {canAddChecklists && !_.isEmpty(dataGridRow) && (
                        <AddCheckListButton displayModalFunc={displayModal} />
                    )}
                </Flex>
            </Flex>
        </>
    );
};

AdminWorkOrderRequirementsChecklists.propTypes = {
    canAddChecklists: PropTypes.bool.isRequired,
    accountId: PropTypes.number,
    checklistOptions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            sections: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    sequencePosition: PropTypes.number,
                    items: PropTypes.arrayOf(
                        PropTypes.shape({
                            description: PropTypes.string,
                            sequencePosition: PropTypes.number,
                        })
                    ),
                })
            ),
        })
    ).isRequired,
    checklists: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            requirement: PropTypes.shape({
                name: PropTypes.string.isRequired,
                description: PropTypes.string.isRequired,
                sections: PropTypes.arrayOf(
                    PropTypes.shape({
                        name: PropTypes.string,
                        sequencePosition: PropTypes.number,
                        items: PropTypes.arrayOf(
                            PropTypes.shape({
                                description: PropTypes.string,
                                sequencePosition: PropTypes.number,
                            })
                        ),
                    })
                ),
            }),
        })
    ),
    handleCompleteChecklistOnClick: PropTypes.func.isRequired,
    handleRemoveChecklistOnClick: PropTypes.func.isRequired,
    handleAddChecklistOnClick: PropTypes.func.isRequired,
    handleRevertChecklistOnClick: PropTypes.func.isRequired,
    handleSaveChecklistOnClick: PropTypes.func.isRequired,
    workOrder: PropTypes.shape({
        statusId: PropTypes.string.isRequired,
    }).isRequired,
};

export default AdminWorkOrderRequirementsChecklists;

const NoChecklists = ({ canAddChecklists, displayModalFunc }) => (
    <StyledAddContainer>
        <span>There are no checklists for this Work Order.</span>
        {canAddChecklists && (
            <AddCheckListButton displayModalFunc={displayModalFunc} />
        )}
    </StyledAddContainer>
);
NoChecklists.propTypes = {
    canAddChecklists: PropTypes.bool.isRequired,
    displayModalFunc: PropTypes.func.isRequired,
};

const AddCheckListButton = ({ displayModalFunc }) => (
    <MotiliButton
        onClick={() => displayModalFunc(modalStateName.addChecklist, [])}
        moStyle='normal'
        text='Add'
        style={{
            width: '96px',
            height: '36px',
            fontWeight: '600',
            marginTop: '12px',
        }}
    />
);
AddCheckListButton.propTypes = {
    displayModalFunc: PropTypes.func.isRequired,
};

const ActionMenu = ({
    checklist,
    workOrder,
    displayModal,
    openActionMenuId,
    setOpenActionMenuId,
}) => {
    const [busy, setBusy] = useState(false);
    if (_.isNil(workOrder) || _.isNil(checklist)) return '';

    if (isContractorAccount()) {
        return (
            <ContractorActionMenu
                checklist={checklist}
                displayModal={displayModal}
            />
        );
    }
    const actionItems = _getActionItems(
        checklist,
        workOrder,
        displayModal,
        setBusy
    );
    const isOpen = openActionMenuId === checklist.id;
    if (_.isEmpty(actionItems)) return '';
    const spinningStyle = {
        paddingRight: '2px',
        marginBottom: '2px',
    };
    return (
        <StyledDotMenuContainger>
            <MotiliTextButtonGroup
                busy={busy}
                style={busy ? spinningStyle : {}}
            >
                <Dropdown
                    show={isOpen}
                    onToggle={nextShow => {
                        setOpenActionMenuId(nextShow ? checklist.id : null);
                    }}
                    id={`checklist-actions-drop-down-${checklist.id}`}
                    disabled={false}
                    bsSize='small'
                >
                    {!busy && (
                        <Dropdown.Toggle
                            as='span'
                            style={{
                                cursor: 'pointer',
                                display: 'inline-block',
                            }}
                            bsPrefix='custom'
                        >
                            <i
                                className='fa fa-lg fa-ellipsis-h'
                                style={{ color: 'rgb(87, 193, 233)' }}
                            />
                        </Dropdown.Toggle>
                    )}
                    <Dropdown.Menu style={{ zIndex: 600 }}>
                        {actionItems.map((action, i) => (
                            <Dropdown.Item
                                key={action.name}
                                eventKey={i}
                                onClick={e => {
                                    e.stopPropagation();
                                    setOpenActionMenuId(null);
                                    action.handleSelect();
                                }}
                                style={{
                                    borderBottom: 'none',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        color:
                                            action.name === 'REMOVE'
                                                ? Config.color.red
                                                : '',
                                    }}
                                >
                                    {action.name}{' '}
                                    {action.sub && (
                                        <span>{` ${action.sub}`}</span>
                                    )}
                                </div>
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </MotiliTextButtonGroup>
        </StyledDotMenuContainger>
    );
};

ActionMenu.propTypes = {
    checklist: PropTypes.object.isRequired,
    workOrder: PropTypes.object.isRequired,
    displayModal: PropTypes.func.isRequired,
};

const ContractorActionMenu = ({ checklist, displayModal }) => {
    const [busy, setBusy] = useState(false);
    const spinningStyle = {
        paddingRight: '10px',
        marginBottom: '3px',
    };
    return (
        <MotiliTextButtonGroup busy={busy} style={busy ? spinningStyle : {}}>
            {!busy && (
                <MotiliTextButton
                    text={
                        <DownloadIconButton
                            onClick={() =>
                                handleDownload({
                                    checklist,
                                    displayModal,
                                    setBusy,
                                })
                            }
                        />
                    }
                />
            )}
        </MotiliTextButtonGroup>
    );
};

const DownloadAllPDFButton = ({ onClick, busy }) => {
    if (busy) {
        return (
            <span style={{ height: '46px', paddingRight: '8px' }}>
                <LoadingSpinner size='medium' busy />
            </span>
        );
    }
    return (
        <span style={{ marginBottom: '8px' }}>
            <StyledDownloadIconButton
                iconColor={Config.color.navy}
                onClick={onClick}
            />
        </span>
    );
};

DownloadAllPDFButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    busy: PropTypes.bool.isRequired,
};

async function handleDownload({ checklist, displayModal, setBusy }) {
    setBusy(true);

    const start = new Date();

    let res;
    try {
        res = await downloadChecklist(checklist);
    } catch (error) {
        setBusy(false);
        Alert.error(
            `An error occured exporting requirement - ${error.message}`
        );
        return;
    }

    const end = new Date();

    displayModal('download', {
        checklists: [res],
        timeElapsed: (end - start) / 1000,
    });

    setBusy(false);
}

function _getActionItems(checklist, workOrder, displayModal, setBusy) {
    const actionItems = [];

    if (isMotiliAdmin()) {
        if (workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS) {
            if (!checklist.meetsClientRequirement) {
                actionItems.push({
                    name: 'COMPLETE',
                    handleSelect: () =>
                        displayModal(modalStateName.complete, checklist),
                });
            } else {
                actionItems.push({
                    name: 'MARK INCOMPLETE',
                    handleSelect: () =>
                        displayModal(modalStateName.revert, checklist),
                });
            }
        }

        actionItems.push({
            name: 'DOWNLOAD',
            handleSelect: () =>
                handleDownload({ checklist, displayModal, setBusy }),
        });

        if (
            !['CANCELLED', 'COMPLETED', 'CLOSED', 'DRAFT_CANCELLED'].includes(
                workOrder.statusId
            )
        ) {
            actionItems.push({
                name: 'REMOVE',
                handleSelect: () =>
                    displayModal(modalStateName.remove, checklist),
            });
        }
    }

    return actionItems;
}

const StyledAddContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    font-size: 14px;
    align-items: center;
    height: 100%;
`;

const StyledChecklistHeader = styled.span`
    font-size: 14px;
`;

const CssChecklistFontValue = css`
    font-size: 14px;
    font-weight: 600;
`;

const StyledChecklistLink = styled.span`
    ${CssChecklistFontValue}
`;

const StyledChecklistValue = styled.span`
    ${CssChecklistFontValue}
    color: ${Config.color.mediumGray};
`;

const StyledDotMenuContainger = styled.div`
    padding-right: 8px;
`;
